import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Upload } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { ErrorState } from '@/components/ui/States';
import { DataTable } from '@/components/ui/Table';
import type { Paginated, ProductRow } from '@/types/api';
import { num } from '@/utils/format';
import { useDebounced } from '@/utils/useDebounced';

/**
 * Products list. Search syncs to the URL (?search=…) so the topbar
 * search box can deep-link here. Tracking-mode is shown as a tiny pill
 * because the cashier on a tablet uses it to remember which products
 * scan-at-bill vs quantity-only.
 */
export function ProductsListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial = params.get('search') ?? '';
  const [search, setSearch] = useState(initial);
  const debouncedSearch = useDebounced(search, 300);

  const query = useQuery({
    queryKey: ['products', { search: debouncedSearch }],
    queryFn: () =>
      apiGet<Paginated<ProductRow> | ProductRow[]>(endpoints.products, {
        search: debouncedSearch || undefined,
      }),
  });

  const rows: ProductRow[] = Array.isArray(query.data)
    ? query.data
    : query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Products"
        description="Your full catalogue. Edit prices, restock, view variants."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Link to="/products/bulk-import">
              <Button variant="outline" iconLeft={<Upload className="h-4 w-4" />}>
                Bulk import
              </Button>
            </Link>
            {/* New-product flow lives on mobile (the wizard is touch-first).
                The web panel is read-and-edit; bulk import covers desktop
                bulk creation. We surface a clear hint instead of a half-
                baked desktop create form. */}
          </div>
        }
      />

      <div className="mb-4">
        <Input
          name="search"
          placeholder="Search by name, SKU or barcode…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            const next = new URLSearchParams(params);
            if (e.target.value) next.set('search', e.target.value);
            else next.delete('search');
            setParams(next, { replace: true });
          }}
          iconLeft={<Search className="h-4 w-4" />}
        />
      </div>

      {query.isError ? (
        <ErrorState
          title="Couldn't load products"
          description={(query.error as Error)?.message}
          onRetry={() => query.refetch()}
        />
      ) : (
        <DataTable
          rowKey={(p) => p.id}
          loading={query.isLoading}
          rows={rows}
          onRowClick={(p) => navigate(`/products/${p.id}`)}
          empty={
            debouncedSearch ? (
              'No products match.'
            ) : (
              <div className="flex flex-col items-center gap-3">
                <span>No products yet — bulk-import a CSV to start.</span>
                <Link to="/products/bulk-import">
                  <Button iconLeft={<Plus className="h-4 w-4" />}>Bulk import</Button>
                </Link>
              </div>
            )
          }
          columns={[
            {
              key: 'name',
              header: 'Product',
              cell: (p) => (
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-ink-soft">
                    {[p.brand?.name, p.category?.name].filter(Boolean).join(' · ') ||
                      'Uncategorised'}
                  </div>
                </div>
              ),
            },
            {
              key: 'sku',
              header: 'SKU',
              hideOnMobile: true,
              cell: (p) => (
                <span className="font-mono text-xs">{p.sku || '—'}</span>
              ),
            },
            {
              key: 'price',
              header: 'Price',
              className: 'text-right',
              cell: (p) => <Money value={p.selling_price} className="font-semibold" />,
            },
            {
              key: 'mrp',
              header: 'MRP',
              className: 'text-right',
              hideOnMobile: true,
              cell: (p) => (p.mrp ? <Money value={p.mrp} /> : '—'),
            },
            {
              key: 'gst',
              header: 'GST',
              hideOnMobile: true,
              cell: (p) => (p.gst_rate ? `${p.gst_rate}%` : '—'),
            },
            {
              key: 'stock',
              header: 'Stock',
              className: 'text-right',
              cell: (p) => {
                const stock = p.stock ?? 0;
                if (stock <= 0) return <Pill tone="danger">Out</Pill>;
                if (stock < 5) return <Pill tone="warn">{num(stock)}</Pill>;
                return <span className="tabular-nums">{num(stock)}</span>;
              },
            },
            {
              key: 'tracking',
              header: 'Tracking',
              hideOnMobile: true,
              cell: (p) => trackingBadge(p.unit_tracking_mode),
            },
          ]}
        />
      )}
    </>
  );
}

function trackingBadge(mode?: string) {
  switch (mode) {
    case 'manufacturer_serial':
      return <Pill tone="brand">Mfr serial</Pill>;
    case 'dukaan_qr':
      return <Pill tone="brand">QR tag</Pill>;
    case 'manual_tag':
      return <Pill tone="warn">Manual tag</Pill>;
    case 'quantity_only':
    default:
      return <Pill tone="neutral">Qty only</Pill>;
  }
}
